/**
 * Panel de Administración de Usuarios (Dashboard Admin).
 * Permite a los administradores gestionar cuentas de usuario, visualizar estadísticas de registro
 * y controlar el acceso de laboratorios e investigadores.
 */

import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Apiurl } from "../../shared/services/apirest";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import bootstrap from "bootstrap/dist/js/bootstrap.bundle";
import "bootstrap-icons/font/bootstrap-icons.css";
import AdminLayout from "./AdminLayout";

function Dashboardadmin() {
  const navigate = useNavigate();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usuarioEliminar, setUsuarioEliminar] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [requestCount, setRequestCount] = useState(0);
  const toastRef = useRef(null);
  const [filterRole, setFilterRole] = useState("");
  const [filterMunicipio, setFilterMunicipio] = useState("");
  const [sortOrder, setSortOrder] = useState("asc"); // 'asc' o 'desc'
  const [currentUserRole, setCurrentUserRole] = useState("");

  /**
   * Retorna la clase de estilo (Bootstrap) según el rol del usuario.
   * @param {string} role - El rol del usuario.
   */
  const getRoleBadgeClass = (role) => {
    const map = {
      admin: "bg-danger",
      admin_lab: "bg-warning text-dark",
      admin_investigacion: "bg-info text-dark",
      laboratorio: "bg-success",
      investigador: "bg-primary",
      visualizador: "bg-secondary",
    };
    return map[role] || "bg-dark";
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userData") || "{}");
    setCurrentUserRole(user.role || "");
  }, []);

  // Memorización de estadísticas para evitar cálculos innecesarios en cada renderizado
  const stats = useMemo(() => ({
    totalLabs: usuarios.filter((u) => u.role === "laboratorio").length,
    totalResearchers: usuarios.filter((u) => u.role === "investigador").length,
    totalAdmins: usuarios.filter((u) => u.role === "admin" || u.role.startsWith("admin_")).length,
    municipios: [...new Set(usuarios.map((u) => u.municipio))].filter(Boolean).length,
  }), [usuarios]);

  /**
   * Muestra una notificación (Toast) en pantalla.
   * @param {string} message - Mensaje a mostrar.
   * @param {string} type - Tipo de notificación (success, danger, warning, info).
   */
  const showToast = (message, type = "success") => {
    const toastEl = toastRef.current;
    if (toastEl) {
      const bsToast = new bootstrap.Toast(toastEl);
      const toastBody = toastEl.querySelector(".toast-body");
      toastBody.textContent = message;
      toastEl.className = `toast align-items-center border-0 text-bg-${type} shadow-lg`;
      bsToast.show();
    }
  };

  /**
   * Obtiene la lista de usuarios y solicitudes pendientes desde la API.
   */
  const obtenerUsuarios = useCallback(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      showToast("⚠️ Sesión no iniciada. Redirigiendo al inicio de sesión...", "warning");
      setTimeout(() => navigate("/Login"), 1500);
      setLoading(false);
      return;
    }

    const config = { headers: { Authorization: `Bearer ${token}` } };

    // Peticiones paralelas para optimizar tiempos de carga
    const usersReq = axios.get(`${Apiurl}users/List_Users/`, config);
    const requestsReq = axios.get(`${Apiurl}users/access-requests/`, config);

    Promise.all([usersReq, requestsReq])
      .then(([usersRes, requestsRes]) => {
        setUsuarios(usersRes.data);
        if (usersRes.data.length === 0) {
          showToast("ℹ️ No hay usuarios registrados en el sistema.", "info");
        }

        // Conteo de solicitudes de acceso pendientes
        const pending = requestsRes.data.filter(r => r.status === 'pendiente').length;
        setRequestCount(pending);
      })
      .catch((err) => {
        console.error("Error al obtener datos:", err);
        if (err.response?.status === 401) {
          showToast("❌ Sesión expirada. Por favor, inicie sesión nuevamente.", "danger");
          localStorage.clear();
          setTimeout(() => navigate("/Login"), 2000);
        } else if (err.response?.status === 403) {
          showToast("❌ No tiene permisos para acceder a esta información.", "danger");
        } else {
          const errorMsg = err.response?.data?.detail || "Error al cargar los datos. Verifique su conexión.";
          showToast(`❌ ${errorMsg}`, "danger");
        }
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  useEffect(() => {
    obtenerUsuarios();
  }, [obtenerUsuarios]);

  /**
   * Ejecuta la eliminación de un usuario tras la confirmación.
   */
  const eliminarUsuario = () => {
    if (!usuarioEliminar) return;
    const token = localStorage.getItem("token");
    const id = usuarioEliminar.id_laboratorios || usuarioEliminar.id;
    const url = `${Apiurl}users/Delete_User/${id}/`;
    const config = { headers: { Authorization: `Bearer ${token}` } };

    axios
      .delete(url, config)
      .then(() => {
        showToast("✅ Usuario eliminado correctamente.", "success");
        setUsuarios((prev) =>
          prev.filter((u) => (u.id_laboratorios || u.id) !== id)
        );
      })
      .catch((err) => {
        console.error("Error al eliminar:", err);
        const msg = err.response?.data?.detail
          || (err.response?.data ? JSON.stringify(err.response?.data) : "")
          || "Error al eliminar el usuario.";
        showToast(msg, "danger");
      });
  };

  // Filtrado y ordenamiento de la lista de usuarios en el cliente
  const filteredUsers = useMemo(() => {
    let result = [...usuarios];

    // 1. Búsqueda por término (Nombre, Email o Municipio)
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(u =>
        u.nombre_laboratorio?.toLowerCase().includes(term) ||
        u.email?.toLowerCase().includes(term) ||
        u.municipio?.toLowerCase().includes(term)
      );
    }

    // 2. Filtro por Rol
    if (filterRole) {
      result = result.filter(u => u.role === filterRole);
    }

    // 3. Filtro por Municipio
    if (filterMunicipio) {
      result = result.filter(u => u.municipio === filterMunicipio);
    }

    // 4. Ordenamiento alfabético
    result.sort((a, b) => {
      const nameA = (a.nombre_laboratorio || "").toLowerCase();
      const nameB = (b.nombre_laboratorio || "").toLowerCase();
      if (sortOrder === "asc") return nameA.localeCompare(nameB);
      return nameB.localeCompare(nameA);
    });

    return result;
  }, [usuarios, searchTerm, filterRole, filterMunicipio, sortOrder]);

  return (
    <AdminLayout
      breadcrumb="Panel de Administración / Usuarios / Listado"
      title="Gestión de Cuentas"
    >
      {/* Contenedor de Notificaciones (Toasts) */}
      <div className="toast-container position-fixed top-0 end-0 p-3" style={{ zIndex: 1055 }}>
        <div ref={toastRef} className="toast" role="alert" aria-live="assertive" aria-atomic="true">
          <div className="d-flex">
            <div className="toast-body fw-semibold"></div>
            <button type="button" className="btn-close me-2 m-auto" data-bs-dismiss="toast" aria-label="Cerrar"></button>
          </div>
        </div>
      </div>

      {/* Grilla de Estadísticas Rápidas */}
      <div className="stats-grid mb-4">
        <div className="stat-card" style={{ cursor: 'pointer' }} onClick={() => navigate('/AdminAccessRequests')}>
          <div className="stat-icon" style={{ backgroundColor: '#fee2e2', color: '#dc2626' }}>
            <i className="bi bi-envelope-exclamation-fill"></i>
          </div>
          <div className="stat-info">
            <h4>{requestCount}</h4>
            <p className="text-danger fw-bold">Solicitudes</p>
          </div>
        </div>

        {(currentUserRole === 'admin' || currentUserRole === 'admin_lab') && (
          <div className="stat-card">
            <div className="stat-icon icon-blue">
              <i className="bi bi-hospital"></i>
            </div>
            <div className="stat-info">
              <h4>{stats.totalLabs}</h4>
              <p>Laboratorios</p>
            </div>
          </div>
        )}

        {(currentUserRole === 'admin' || currentUserRole === 'admin_investigacion') && (
          <div className="stat-card">
            <div className="stat-icon icon-green">
              <i className="bi bi-people-fill"></i>
            </div>
            <div className="stat-info">
              <h4>{stats.totalResearchers}</h4>
              <p>Investigadores</p>
            </div>
          </div>
        )}

        {currentUserRole === 'admin' && (
          <div className="stat-card">
            <div className="stat-icon icon-purple">
              <i className="bi bi-shield-lock-fill"></i>
            </div>
            <div className="stat-info">
              <h4>{stats.totalAdmins}</h4>
              <p>Administradores</p>
            </div>
          </div>
        )}

        <div className="stat-card">
          <div className="stat-icon icon-purple">
            <i className="bi bi-geo-alt"></i>
          </div>
          <div className="stat-info">
            <h4>{stats.municipios}</h4>
            <p>Municipios</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon icon-orange">
            <i className="bi bi-activity"></i>
          </div>
          <div className="stat-info">
            <h4>Activo</h4>
            <p>Estado Sistema</p>
          </div>
        </div>
      </div>

      {/* Barra de Herramientas (Búsqueda y Filtros) */}
      <div className="toolbar flex-wrap gap-3">
        <div className="search-wrapper flex-grow-1" style={{ minWidth: '250px' }}>
          <i className="bi bi-search"></i>
          <input
            type="text"
            className="search-input"
            placeholder="Buscar por nombre o correo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="d-flex gap-2 flex-wrap align-items-center">
          <select
            className="form-select shadow-sm"
            style={{ width: 'auto' }}
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
          >
            <option value="">Todos los Roles</option>
            <option value="laboratorio">Laboratorio</option>
            <option value="investigador">Investigador</option>
            <option value="admin_lab">Admin Laboratorios</option>
            <option value="admin_investigacion">Admin Investigadores</option>
            <option value="admin">Super Admin</option>
            <option value="visualizador">Visualizador</option>
          </select>

          <select
            className="form-select shadow-sm"
            style={{ width: 'auto' }}
            value={filterMunicipio}
            onChange={(e) => setFilterMunicipio(e.target.value)}
          >
            <option value="">Todas las Ubicaciones</option>
            {[...new Set(usuarios.map(u => u.municipio))].filter(Boolean).sort().map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>

          <select
            className="form-select shadow-sm"
            style={{ width: 'auto' }}
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          >
            <option value="asc">A-Z (Nombre)</option>
            <option value="desc">Z-A (Nombre)</option>
          </select>

          <button className="btn btn-primary d-flex align-items-center gap-2" onClick={() => navigate("/New")} title="Nuevo Usuario">
            <i className="bi bi-plus-lg"></i>
          </button>
        </div>
      </div>

      {/* Tabla de Usuarios */}
      <div className="table-container">
        <div className="table-responsive">
          <table className="table admin-table align-middle">
            <thead>
              <tr>
                <th>Laboratorio / Usuario</th>
                <th>Cód. Lab</th>
                <th>Ubicación</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="4" className="text-center py-5">
                    <div className="spinner-border text-primary" role="status"></div>
                    <p className="mt-2 text-muted">Cargando base de datos...</p>
                  </td>
                </tr>
              ) : filteredUsers.length > 0 ? (
                filteredUsers.map((u) => (
                  <tr key={u.id_laboratorios || u.id}>
                    <td>
                      <span className="lab-name">{u.nombre_laboratorio}</span>
                      <span className="lab-email">{u.email}</span>
                      <span className={`badge ${getRoleBadgeClass(u.role)} mt-1 text-uppercase`} style={{ fontSize: '0.7rem' }}>
                        {u.role.replace('_', ' ')}
                      </span>
                    </td>
                    <td>
                      <code className="text-dark fw-bold">{u.cod_laboratorio || '---'}</code>
                    </td>
                    <td>
                      <div className="fw-bold text-dark">{u.municipio || '---'}</div>
                      <div className="small text-muted">{u.departamento || '---'}</div>
                    </td>
                    <td>
                      <div className="action-btns">
                        <button
                          className="action-btn btn-edit"
                          title="Editar Registro"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/Edit/${u.id_laboratorios || u.id}`);
                          }}
                        >
                          <i className="bi bi-pencil-fill"></i>
                        </button>
                        <button
                          className="action-btn btn-delete"
                          title="Eliminar Registro"
                          data-bs-toggle="modal"
                          data-bs-target="#confirmModal"
                          onClick={(e) => {
                            e.stopPropagation();
                            setUsuarioEliminar(u);
                          }}
                        >
                          <i className="bi bi-trash-fill"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center py-5 text-muted">
                    No se encontraron registros que coincidan con la búsqueda.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de Confirmación de Eliminación */}
      <div className="modal fade" id="confirmModal" tabIndex="-1" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 shadow-lg rounded-4">
            <div className="modal-header border-0 pb-0">
              <h5 className="modal-title fw-bold">Confirmar Eliminación</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body py-4">
              <p className="mb-0 text-muted">
                ¿Está seguro que desea eliminar a <strong>{usuarioEliminar?.nombre_laboratorio || usuarioEliminar?.email}</strong>?
                Esta acción no se puede deshacer y el acceso quedará revocado permanentemente.
              </p>
            </div>
            <div className="modal-footer border-0 pt-0">
              <button type="button" className="btn btn-light" data-bs-dismiss="modal">Cancelar</button>
              <button
                type="button"
                className="btn btn-danger px-4"
                data-bs-dismiss="modal"
                onClick={eliminarUsuario}
              >
                Eliminar Registro
              </button>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

export default Dashboardadmin;

